import { useCallback, useEffect, useState } from 'react';
import { currentTreeDrag } from './useTreeDnd';

const WS_MIME = 'application/x-apiboo-docscope';
const LEAVE_GRACE_MS = 300;

/** Where a tree row may be dropped in the switcher: Personal or a workspace, not the tree it is in. */
export function acceptsTreeDrop(choice, drag) {
  if (!drag || !choice) return false;
  if (choice.kind !== 'personal' && choice.kind !== 'workspace') return false;
  const fromPersonal = !drag.scope?.type || drag.scope.type === 'personal';
  return fromPersonal && drag.sectionId !== choice.key;
}

/** `ids` with `movedId` placed before / after `targetId`. */
export function reorderIds(ids, movedId, targetId, after) {
  const rest = ids.filter((id) => id !== movedId);
  const at = rest.indexOf(targetId);
  if (at < 0) return ids;
  rest.splice(after ? at + 1 : at, 0, movedId);
  return rest;
}

/**
 * Drag and drop on the workspace switcher (cards 5.2 / 5.3):
 *
 * - A TREE row dragged onto the switcher button opens the menu; dropped on
 *   Personal or a workspace row, the row's whole tree moves there
 *   (`onMoveTree(drag, choice)`). Leaving the menu closes it again.
 * - A WORKSPACE row dragged within the menu reorders "My workspaces"
 *   (`onReorder(ids)`).
 *
 * Returns the picker's `forceOpen`, `onOpenChange`, `triggerProps` and
 * `rowProps(option)`; an option carries its `choice`.
 */
export default function useSwitcherDnd({ choices, onMoveTree, onReorder }) {
  const [forceOpen, setForceOpen] = useState(false);
  const [over, setOver] = useState(null); // { key, mode: 'move' | 'before' | 'after' }
  const [wsDrag, setWsDrag] = useState(null); // dragged workspace key

  const reset = useCallback(() => {
    setForceOpen(false);
    setOver(null);
    setWsDrag(null);
  }, []);

  // A drag that ends anywhere, or stays off the menu (on the backdrop) for a
  // moment, closes a menu the drag opened. The grace covers the few pixels
  // between the button and the menu.
  useEffect(() => {
    if (!forceOpen) return undefined;
    let timer = null;
    const onOver = (e) => {
      if (e.target?.closest?.('.doc-scope-menu, .doc-scope-picker')) {
        clearTimeout(timer);
        timer = null;
      } else if (!timer) timer = setTimeout(reset, LEAVE_GRACE_MS);
    };
    window.addEventListener('dragover', onOver, true);
    window.addEventListener('dragend', reset, true);
    window.addEventListener('drop', reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('dragover', onOver, true);
      window.removeEventListener('dragend', reset, true);
      window.removeEventListener('drop', reset);
    };
  }, [forceOpen, reset]);

  const triggerProps = {
    onDragEnter: (e) => {
      if (!currentTreeDrag()) return;
      e.preventDefault();
      setForceOpen(true);
    },
    onDragOver: (e) => {
      if (!currentTreeDrag()) return;
      // Not a drop target itself (dropEffect none) — it only opens the menu.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
      setForceOpen(true);
    },
  };

  const workspaceIds = choices.filter((c) => c.kind === 'workspace').map((c) => c.key);

  const rowProps = (option) => {
    const choice = option.choice;
    const isWs = choice?.kind === 'workspace';
    const marker = over?.key === choice?.key ? over.mode : undefined;
    return {
      'data-testid': `doc-scope-option-${choice?.key}`,
      'data-drop': marker,
      draggable: isWs || undefined,
      onDragStart: isWs
        ? (e) => {
            e.stopPropagation();
            setWsDrag(choice.key);
            try {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(WS_MIME, choice.key);
            } catch {
              /* jsdom */
            }
          }
        : undefined,
      onDragEnd: isWs ? () => { setWsDrag(null); setOver(null); } : undefined,
      onDragOver: (e) => {
        const drag = currentTreeDrag();
        if (drag) {
          if (!acceptsTreeDrop(choice, drag)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (marker !== 'move') setOver({ key: choice.key, mode: 'move' });
          return;
        }
        if (!wsDrag || !isWs || wsDrag === choice.key) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const mode = e.clientY > rect.top + rect.height / 2 ? 'after' : 'before';
        if (marker !== mode) setOver({ key: choice.key, mode });
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setOver((prev) => (prev?.key === choice?.key ? null : prev));
      },
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const drag = currentTreeDrag();
        const mode = over?.key === choice?.key ? over.mode : null;
        if (drag && acceptsTreeDrop(choice, drag)) {
          reset();
          onMoveTree(drag, choice);
          return;
        }
        if (wsDrag && isWs && wsDrag !== choice.key) {
          const ids = reorderIds(workspaceIds, wsDrag, choice.key, mode === 'after');
          setWsDrag(null);
          setOver(null);
          onReorder(ids.map((key) => choices.find((c) => c.key === key)?.scopeId).filter(Boolean));
        }
      },
    };
  };

  return {
    forceOpen,
    onOpenChange: (open) => {
      if (!open) reset();
    },
    triggerProps,
    rowProps,
  };
}
